import React from 'react';
import {
  Loading,
  RecordContextProvider,
  useGetOne,
  useRecordContext,
} from 'react-admin';
import { Grid } from '@mui/material';

import auditTrackDiffGenerator, { AuditTrack } from '../audit/auditDiffGenerator/auditTrackDiffGenerator';
import AuditExtraInfo from '../audit/AuditExtraInfo';
import NoResults from '../layout/NoResults';
import TextDiffField from '../utils/auditTracks/TextDiffField';

type ITransactionType = {
  name: string;
  active: boolean;
};

const TransactionTypeAuditDetails = () => {
  const record = useRecordContext<AuditTrack<ITransactionType>>();
  const { data, isLoading } = useGetOne<AuditTrack<ITransactionType>>(
    `audit-tracks/${record?.id}/previous`,
    { id: '' },
    { enabled: !!record },
  );

  if (!record) return <NoResults />;

  if (!data) {
    if (isLoading) return <Loading />;
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const isElementCreated = data.id === record.id;

  if (!record.documentAfter || (!isElementCreated && !data.documentAfter)) {
    if (record.extraInfo) return <AuditExtraInfo />;
    return <NoResults />;
  }

  const auditTracksDiff = auditTrackDiffGenerator<ITransactionType>({
    current: record,
    previous: !isElementCreated ? data : undefined,
  });

  const { resourceData } = auditTracksDiff;

  const hasFieldChanges = {
    name: resourceData.name?.hasChanged,
    active: resourceData.active?.hasChanged,
  };

  const hasAnyChanges = Object.values(hasFieldChanges).some((field) => field);

  return (
    <Grid container spacing={6}>
      {!hasAnyChanges && <NoResults text="Only system changes detected" />}
      {hasAnyChanges && (
        <RecordContextProvider value={resourceData}>
          {hasFieldChanges.name && <TextDiffField source="name" label="Name" />}
          {hasFieldChanges.active && <TextDiffField source="active" label="Active" />}
        </RecordContextProvider>
      )}
      {record.extraInfo && <AuditExtraInfo />}
    </Grid>
  );
};

export default TransactionTypeAuditDetails;
